/**
 * The header "Artifacts" menu — every artifact this conversation produced, in one list, grouped by type.
 *
 * The pinned card (see `PinnedArtifactCard`) keeps the MOST RECENT artifact reachable; this is the way back
 * to any of the earlier ones, which is the other half of ACT2-538: scrolling a long transcript hunting for
 * the card that created a dashboard is not a route.
 *
 * Rows are grouped rather than interleaved because the two types answer different questions ("which
 * dashboard did we build?" vs "which audience?"), and a flat transcript-ordered list makes you read every
 * row to answer either. The type icon lives on the section heading, not on every row — repeating it next
 * to each title is noise once the heading already says which kind you are in.
 */
import { Blocks, LayoutDashboard, Users } from 'lucide-react';
import { Fragment, useId } from 'react';
import { Link } from 'react-router-dom';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { exact } from '@/activations/lib/run-view';
import { AudienceStatusBadge } from '@/audiences/components/AudienceStatusBadge';
import { AUDIENCE_MISSING_ID_REASON, audienceHref } from '@/audiences/lib/audience-nav';
import type { ChatArtifact, ChatArtifactType } from '@/chat/lib/chat-artifacts';
import type { DashboardArtifactOpenHint } from '@/chat/lib/dashboard-draft';
import { panelCountLabel } from '@/dashboard/lib/dashboardList';

interface ChatArtifactsMenuProps {
  artifacts: ChatArtifact[];
  /** Slug of the dashboard whose preview panel is currently open — marks that row as current. */
  activeDashboardSlug?: string | null;
  /** Dashboards open their in-chat preview panel rather than navigating away, so the host handles them. */
  onDashboardSelect: (slug: string, hint?: DashboardArtifactOpenHint) => void;
}

const GROUPS: { type: ChatArtifactType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { type: 'audience', label: 'Audiences', icon: Users },
  { type: 'dashboard', label: 'Dashboards', icon: LayoutDashboard },
];

/** The row's second line — the same detail each type's card shows below its title. */
function rowDetail(artifact: ChatArtifact): string | null {
  switch (artifact.type) {
    case 'dashboard':
      return artifact.panelCount === undefined ? null : panelCountLabel(artifact.panelCount);
    case 'audience':
      // `exact` pins the grouping to `en`; a bare toLocaleString reads as a decimal point in de-DE.
      return artifact.countState.status === 'counted'
        ? `${exact(artifact.countState.members)} ${artifact.countState.members === 1 ? 'member' : 'members'}`
        : null;
  }
}

/**
 * Why this row cannot be opened, or null when it can. Both surfaces of the same rule: a row that leads
 * nowhere stays visible and disabled WITH a reason, rather than silently doing nothing on click.
 */
function rowBlockedReason(artifact: ChatArtifact): string | null {
  switch (artifact.type) {
    case 'dashboard':
      // Without a live draft or a saved dashboard to hydrate, opening the preview reaches
      // `setActive`, whose unknown-slug guard returns silently — a click that does nothing at all.
      return artifact.previewAvailable ? null : 'This dashboard was never finished, so there is no preview to open.';
    case 'audience':
      return artifact.audienceId === null ? AUDIENCE_MISSING_ID_REASON : null;
  }
}

/** The row's status chip — the same one each type's card shows next to its title. */
function rowBadge(artifact: ChatArtifact): React.ReactNode {
  switch (artifact.type) {
    case 'dashboard':
      return artifact.isDraft ? (
        <Badge variant="warning" bordered className="shrink-0">
          Draft
        </Badge>
      ) : null;
    case 'audience':
      return <AudienceStatusBadge status={artifact.status} />;
  }
}

export function ChatArtifactsMenu({
  artifacts,
  activeDashboardSlug = null,
  onDashboardSelect,
}: ChatArtifactsMenuProps): React.ReactElement | null {
  const { wp } = useWorkspace();
  const headingId = useId();

  if (artifacts.length === 0) return null;

  const groups = GROUPS.map((group) => ({
    ...group,
    items: artifacts.filter((artifact) => artifact.type === group.type),
  })).filter((group) => group.items.length > 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          label="Artifacts"
          Icon={<Blocks />}
          aria-label={`Artifacts (${artifacts.length} ${artifacts.length === 1 ? 'item' : 'items'})`}
        />
      </DropdownMenuTrigger>
      {/* Outer clips to radius; inner scrolls so the scrollbar stays inside the panel. */}
      <DropdownMenuContent align="end" className="w-72 overflow-hidden p-0">
        <div className="max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto overscroll-contain p-1.5">
          {groups.map((group, index) => {
            const Icon = group.icon;
            return (
              <Fragment key={group.type}>
                {index > 0 ? <DropdownMenuSeparator /> : null}
                {/* `role="group"` is nameless without this pairing, which would flatten the grouping to a
                    visual-only cue and hand a screen reader the undifferentiated list it exists to avoid. */}
                <DropdownMenuGroup aria-labelledby={`${headingId}-${group.type}`}>
                  <DropdownMenuLabel
                    id={`${headingId}-${group.type}`}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground"
                  >
                    <Icon className="size-3.5" data-testid={`artifact-group-icon-${group.type}`} aria-hidden />
                    {group.label}
                  </DropdownMenuLabel>
                  {group.items.map((artifact) => {
                    const detail = rowDetail(artifact);
                    const isActive = artifact.type === 'dashboard' && artifact.slug === activeDashboardSlug;
                    // Two ways a row can lead nowhere, and both must say so rather than no-op on click:
                    // a malformed audience payload carries no id, and a dashboard whose preview cannot
                    // open lands in `setActive`'s unknown-slug guard, which returns silently.
                    const blocked = rowBlockedReason(artifact);
                    const unreachable = blocked !== null;
                    const reasonId = `${headingId}-reason-${artifact.key}`;
                    // `aria-disabled`, NOT Radix's `disabled` (ui skill rule 18): `disabled` sets
                    // `focusable: false` on the roving-focus item, so arrow keys skip the row entirely and
                    // its tooltip — the whole point of `rowBlockedReason` — can never be reached without a
                    // pointer (Radix also `preventDefault`s Tab inside menu content, so the tooltip's own
                    // trigger span is unreachable too). Focusable means the row must refuse its own
                    // activation, and `preventDefault` additionally keeps the menu open on a blocked pick.
                    const handleSelect = (event: Event): void => {
                      if (unreachable) {
                        event.preventDefault();
                        return;
                      }
                      if (artifact.type === 'dashboard') {
                        onDashboardSelect(artifact.slug, {
                          title: artifact.title,
                          saved: artifact.saved,
                          ...(artifact.dashboardId === undefined ? {} : { dashboardId: artifact.dashboardId }),
                        });
                      }
                    };
                    const body = (
                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className="truncate font-medium text-foreground">{artifact.title}</span>
                          {rowBadge(artifact)}
                        </span>
                        {detail ? (
                          <span className="mt-0.5 block truncate text-xs text-muted-foreground">{detail}</span>
                        ) : null}
                      </span>
                    );
                    return (
                      // `keepMounted`: `unreachable` is derived from the live dashboard store, so it can flip
                      // while the menu is open. Without it ActionTooltip swaps a bare fragment for its
                      // wrapper subtree, remounting the row and dropping a keyboard user's place.
                      <ActionTooltip
                        key={artifact.key}
                        show={unreachable}
                        keepMounted
                        message={blocked ?? ''}
                        className="block"
                      >
                        <DropdownMenuItem
                          className="h-auto w-full py-2"
                          aria-disabled={unreachable || undefined}
                          // The tooltip opens on focus (focusin bubbles to its trigger span), but Radix hangs
                          // its `aria-describedby` on that span rather than on the row — so the reason is
                          // wired to the row itself, and screen readers announce it on arrow-key focus.
                          aria-describedby={unreachable ? reasonId : undefined}
                          onSelect={handleSelect}
                          // A dashboard opens the in-chat preview panel — a callback, not a route — so only
                          // audiences can be links. `asChild` gives them a real href, which the imperative
                          // navigate did not: cmd-click and "open in new tab" now work.
                          {...(artifact.type === 'audience' ? { asChild: !unreachable } : {})}
                          aria-current={isActive ? 'true' : undefined}
                        >
                          {artifact.type === 'audience' && artifact.audienceId !== null ? (
                            <Link to={audienceHref(wp, artifact.audienceId)} className="flex min-w-0 flex-1">
                              {body}
                            </Link>
                          ) : (
                            body
                          )}
                        </DropdownMenuItem>
                        {/* Outside the row, so it reads as a DESCRIPTION rather than joining its name. */}
                        {blocked ? (
                          <span id={reasonId} className="sr-only">
                            {blocked}
                          </span>
                        ) : null}
                      </ActionTooltip>
                    );
                  })}
                </DropdownMenuGroup>
              </Fragment>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
